import {
  ACTION_SELECT_BUSINESS_EXP_LIST_RESULT,
  ACTION_SHOW_FULL_MAP,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { CollectionMatchParams } from 'utils/collectionPageUtils';
import { LocalDateTime } from 'js-joda';
import { LockOptions, lib } from 'types';
import { PatronLocationStateContext } from 'components/patron-location/PatronLocationContext';
import {
  SearchMode,
  SearchQueryStringParams,
  buildQueryStringParams,
  getCollectionPageDatesFromConfig,
  isValidQuery,
} from 'components/search/utils';
import { SearchRequestParams } from 'utils/search/buildSearchRequestParams';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { consumer, core } from '@tock/coretypes';
import { ga4SendEvent } from 'components/consent-restricted-scripts/GoogleAnalytics';
import { getAnalyticsEventType } from 'utils/analytics/amplitude';
import { getIsLandScapeOrientation } from 'utils/screen';
import {
  getPurchaseSizeFromPartySize,
  offeringConsumerDetailPageRelativeLink,
  offeringConsumerMenuPageRelativeLink,
} from '@tock/shared/utils/offerings';
import { lockSelector } from 'selectors/lockSelectors';
import { search } from 'actions/AvailabilityActions';
import { stringify } from '@tock/query-string';
import { tryLockAndContinueCitySearch } from 'utils/lock';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useApp } from 'selectors/appSelector';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useCurrentDayRef } from 'hooks/search/useCurrentDayRef';
import { useCurrentMetroArea } from 'hooks/useCurrentMetroArea';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router';
import { usePrevious } from '@tock/hooks';
import { useQueryStringParams } from 'hooks/useQueryStringParams';
import { useRoutedCollectionPageInfo } from 'selectors/consumerPageSelectors';
import { useSearchMode } from 'hooks/search/useSearchMode';
import { useSearchRequest } from 'hooks/search/useSearchRequest';
import _ from 'lodash';

export interface AnalyticsParams {
  ticketTypeName?: string;
  businessListIndex?: number;
}

export type AnalyticsSearchContext =
  | 'Business group search'
  | 'To Go search'
  | 'Collection page search'
  | 'Citywide search';

export type SearchHandlers = {
  selectedBusinessId: number | undefined;
  setSelectedBusinessId: React.Dispatch<React.SetStateAction<number | undefined>>;
  isFullMapView: boolean;
  openFullMapView: () => void;
  closeFullMapView: () => void;
  updateSearchUrl: (newParams: Partial<SearchQueryStringParams>) => void;
  navigateToExperience: (
    business: consumer.OfferingBusiness,
    experience: consumer.Offering,
    experienceListIdx?: number,
    businessListIdx?: number,
    date?: string
  ) => void;
  navigateToExperienceMenu: (
    business: consumer.OfferingBusiness,
    experience: consumer.Offering,
    idx?: number
  ) => void;
  getUrlPostfix(): string;
  lockTimeAndSize(
    ticketTypeId: number,
    selectedSize: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions,
    business: consumer.OfferingBusiness,
    analyticsParams?: AnalyticsParams
  ): Promise<void>;
  startOrder: () => void;
  mode?: SearchMode;
  analyticsSearchContext: AnalyticsSearchContext;
  availability: StoreType['availability'];
  searchQuery?: SearchRequestParams;
  header?: string;
};

interface MatchParams {
  businessGroupSlug?: string;
  citySlug?: string;
}

/**
 * This is initialized at the root of the search page with the search mode and put on
 * search context. It can only be used from that context, and not as another instance of this hook.
 * @param {SearchMode} mode
 */
export function useSearchHandlers(type: SearchMode['stateType']): SearchHandlers {
  const currentMetroArea = useCurrentMetroArea();
  const history = useHistory();
  const queryStringParams = useQueryStringParams<SearchQueryStringParams>();
  const [selectedBusinessId, setSelectedBusinessId] = useState<number | undefined>(undefined);
  const [isFullMapView, setFullMapView] = useState<boolean>(false);
  const app = useApp();
  const lock = useSelector(lockSelector);
  const { actions } = useActions();
  const mode = useSearchMode(type);
  const match = useRouteMatch<MatchParams & CollectionMatchParams>();
  const dispatch = useDispatch();
  const { currentPatronLocation } = useContext(PatronLocationStateContext);
  const { availability, searchQuery } = useSearchRequest(mode);
  const prevMetroArea = usePrevious(currentMetroArea);
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const today = useCurrentDayRef(currentMetroArea?.timezone);

  const collectionPageInfo = useRoutedCollectionPageInfo();

  useEffect(() => {
    const findCityBySlug = (slug: string) =>
      ((app.config?.metroArea ?? []) as core.MetroArea[]).find((m) => m.slug === slug);

    // a new current metro area was selected outside of search,
    // it doesn't match the previous metro
    // and it doesn't match the current route match slug
    // so navigate
    if (
      currentMetroArea &&
      prevMetroArea &&
      currentMetroArea.slug !== prevMetroArea.slug &&
      match.params.citySlug &&
      currentMetroArea.slug !== match.params.citySlug
    ) {
      history.push({
        pathname: `/city/${currentMetroArea.slug}/search`,
        search: stringify({
          ...queryStringParams,
          city: currentMetroArea.name,
          topLeftLat: undefined,
          topLeftLon: undefined,
          bottomRightLat: undefined,
          bottomRightLon: undefined,
        }),
      });
      // the current slug doesn't match the current metro, so select the new metro
    } else if (match.params.citySlug && match.params.citySlug !== currentMetroArea?.slug) {
      const urlMetro = findCityBySlug(match.params.citySlug);
      if (urlMetro) {
        actions.metroArea.selectMetroArea(urlMetro);
      }
      // If viewing a Collection Page, neither of the above conitions will be triggered by a metro area change due to
      // the lack of a city "slug" in the Collection Page URL. Instead, we handle this case like so, and change the
      // 'city' query string param directly
    } else if (
      mode?.stateType === 'collectionPage' &&
      currentMetroArea?.name !== queryStringParams.city
    ) {
      // Use history.replace to prevent interfering with browser history
      history.replace({
        search: stringify({
          ...queryStringParams,
          city: currentMetroArea?.name,
          topLeftLat: undefined,
          topLeftLon: undefined,
          bottomRightLat: undefined,
          bottomRightLon: undefined,
        }),
      });
    }
  }, [
    actions.metroArea,
    app.config?.metroArea,
    currentMetroArea,
    history,
    match.params.citySlug,
    mode?.stateType,
    prevMetroArea,
    queryStringParams,
  ]);

  useEffect(() => {
    const filledInQueryStringParams = mode
      ? buildQueryStringParams({
          queryStringParams,
          mode,
          patronLatLng: currentPatronLocation
            ? { lat: currentPatronLocation?.lat, lng: currentPatronLocation?.lng }
            : undefined,
        })
      : undefined;

    // there is a patron location but it's not in the url, so update it and return. this will cause this to come back
    // here and it will then be in the url to execute a search
    if (
      filledInQueryStringParams?.latlng &&
      filledInQueryStringParams.latlng !== queryStringParams.latlng
    ) {
      history.replace({
        search: stringify({
          ...filledInQueryStringParams,
        }),
      });
    }
  }, [history, mode, currentPatronLocation, queryStringParams]);

  // If rendering a Collection Page, update the URL query string as necessary for the initial load
  useEffect(() => {
    if (!mode) {
      return;
    }
    // Short circuit if not rendering a Collection page
    if (mode && mode['stateType'] !== 'collectionPage') {
      return;
    }

    const filledInQueryStringParams = mode
      ? buildQueryStringParams({
          queryStringParams,
          mode,
        })
      : undefined;

    const collectionPageHasDateParams = collectionPageInfo.dateStart || collectionPageInfo.dateEnd;

    const shouldUpdateCollectionDateParams =
      !filledInQueryStringParams?.date && collectionPageHasDateParams;

    const shouldUpdateOfferingTypeParams =
      !filledInQueryStringParams?.type && collectionPageInfo.offeringSearchType;

    if (!shouldUpdateCollectionDateParams && !shouldUpdateOfferingTypeParams) {
      return;
    }

    const { collectionDate, collectionDateEnd } = getCollectionPageDatesFromConfig(
      collectionPageInfo,
      today
    );

    const dateParams: SearchQueryStringParams = shouldUpdateCollectionDateParams
      ? {
          date: collectionDate.toString(),
          dateEnd: collectionDateEnd?.toString(),
        }
      : {};

    const typeParams: SearchQueryStringParams = shouldUpdateOfferingTypeParams
      ? {
          type: collectionPageInfo.offeringSearchType,
        }
      : {};

    history.replace({
      search: stringify({
        ...filledInQueryStringParams,
        ...dateParams,
        ...typeParams,
      }),
    });
  }, [collectionPageInfo, history, mode, queryStringParams, today]);

  const loadCurrentSearch = () => {
    if (!searchQuery) {
      return;
    }
    dispatch(
      search(searchQuery.query, {
        enableCache: false,
        additionalQuery: searchQuery.additionalQuery,
      })
    );
  };

  const handleOrientationChange = useCallback(() => {
    const isLandscape = getIsLandScapeOrientation();
    if (isLandscape) {
      setFullMapView(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openFullMapView = () => {
    window.analytics.sendEvent(
      'Citywide search',
      'Citywide search full map view shown from floating map button',
      `${currentMetroArea?.name}`
    );
    const data = {
      currentMetroArea: currentMetroArea?.name,
      searchContext: getAnalyticsEventType(mode),
    };
    amplitudeSendEvent(ACTION_SHOW_FULL_MAP, data);
    ga4SendEvent('show_full_map', data);

    if (getIsLandScapeOrientation()) {
      return;
    }

    window.analytics.sendEvent(
      'Citywide search',
      'Citywide search full map view shown',
      `${currentMetroArea?.name}`
    );

    setFullMapView(true);
  };

  const closeFullMapView = () => setFullMapView(false);

  const getUrlPostfix = (): string => {
    if (mode?.stateType === 'city') {
      return `/city/${mode.metroArea.slug}/search`;
    }
    if (mode?.stateType === 'collectionPage') {
      return `/c/${match.params.collection}`;
    }
    if (mode?.stateType === 'group') {
      return `/group/${mode.businessGroup.slug}/search`;
    }
    if (mode?.stateType === 'togo') {
      return '/togo/search';
    }
    return '';
  };

  const updateSearchUrl = (newParams: Partial<SearchQueryStringParams>) => {
    if (!mode) {
      return;
    }

    const newQueryStringParams = { ...queryStringParams, ...newParams };

    const newQueryString = buildQueryStringParams({
      queryStringParams: { ...newQueryStringParams },
      mode,
    });

    removeParamsNotRequired(newQueryString);

    // todo update this to throw an error, it's really hard to know when you're hitting it
    if (!isValidQuery(newQueryString, mode, currentMetroArea)) {
      return;
    }

    if (_.isEqual(queryStringParams, newQueryString)) {
      return;
    }

    history.push({
      pathname: getUrlPostfix(),
      search: stringify({
        ...newQueryString,
      }),
    });
  };

  const navigateToExperience = (
    business: consumer.OfferingBusiness,
    experience: consumer.Offering,
    experienceListIdx?: number,
    businessListIdx?: number,
    date?: string
  ) => {
    window.analytics.sendEvent(
      'Citywide search',
      'Citywide search Experience detail selected',
      `${business.name} - ${experience.name} ${experienceListIdx ? ' - ' + experienceListIdx : ''}`
    );
    const data = {
      businessName: business.name,
      experienceName: experience.name,
      businessListIndex: businessListIdx,
      experienceListIndex: experienceListIdx,
      searchContext: getAnalyticsEventType(mode),
      offeringType: experience.type,
    };
    amplitudeSendEvent(ACTION_SELECT_BUSINESS_EXP_LIST_RESULT, data);
    ga4SendEvent('select_business_experience_list_result', data);
    actions.availability.resetSsrInitialization();
    if (experience.hasDetailPage) {
      if (app.fastRoutingBetweenContext) {
        history.push(
          `/${business.domainName}${offeringConsumerDetailPageRelativeLink(experience)}`
        );
      } else {
        // When having a reservation lock, do a full page refresh to load checkout flow.
        // eslint-disable-next-line xss/no-location-href-assign
        window.location.href = `/${business.domainName}${offeringConsumerDetailPageRelativeLink(
          experience
        )}`;
      }
    } else {
      const size = getPurchaseSizeFromPartySize(
        queryStringParams.size ? Number(queryStringParams.size) : 2,
        experience.singleUnitQuantity
      );

      // can we use someting besides size here? maybe experience size instead of size?
      // that way we can not execute a search when size changes, and know better what is happening
      // via url state of query string params (KM 2021-02-26)
      history.push({
        pathname: location.pathname,
        search: stringify({
          ...queryStringParams,
          size,
          date: date ? date : queryStringParams.date,
          experienceId: experience.id,
          businessId: business.id,
          businessDomain: business.domainName,
          showModal: 'experience',
        }),
      });
    }
  };

  const navigateToExperienceMenu = (
    business: consumer.OfferingBusiness,
    experience: consumer.Offering,
    idx?: number
  ) => {
    if (experience.hasDetailPage) {
      if (app.fastRoutingBetweenContext) {
        history.push(`/${business.domainName}${offeringConsumerMenuPageRelativeLink(experience)}`);
      } else {
        // When having a reservation lock, do a full page refresh to load checkout flow.
        // eslint-disable-next-line xss/no-location-href-assign
        window.location.href = `/${business.domainName}${offeringConsumerMenuPageRelativeLink(
          experience
        )}`;
      }
    } else {
      window.analytics.sendEvent(
        'Citywide search',
        'Citywide search Experience menu detail selected',
        `${business.name} - ${experience.name} ${idx ? ' - ' + idx : ''}`
      );

      history.push({
        pathname: location.pathname,
        search: stringify({
          ...queryStringParams,
          experienceId: experience.id,
          businessId: business.id,
          businessDomain: business.domainName,
          showModal: 'menu',
        }),
      });
    }
  };

  const startOrder = () => {
    history.replace({
      pathname: location.pathname,
      search: stringify({
        ...queryStringParams,
        showModal: 'experience',
      }),
    });
  };

  const lockTimeAndSize = async (
    ticketTypeId: number,
    selectedSize: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions,
    business: consumer.OfferingBusiness,
    analyticsParams?: AnalyticsParams
  ) => {
    if (!queryStringParams.showModal) {
      window.analytics.sendEvent(
        'Citywide search',
        'Experience time selected',
        `${business.name} - ${analyticsParams?.ticketTypeName}`,
        analyticsParams?.businessListIndex || null
      );
    }

    try {
      // this needs to be cleaned up in terms of just sending actions, and it
      // being tied to selection in redux
      await tryLockAndContinueCitySearch(
        actions,
        ticketTypeId,
        selectedSize,
        lock,
        dateTime,
        priceCents,
        options,
        business
      );

      const query: Dictionary<string> = {
        tock_source: 'tock',
        tock_medium: 'availability_search',
        tock_content: 'available_time',
      };
      if (app.fastRoutingBetweenContext) {
        history.push(`/${business.domainName}/checkout/edit-guest-profile?${stringify(query)}`);
      } else {
        // When having a reservation lock, do a full page refresh to load checkout flow.
        // eslint-disable-next-line xss/no-location-href-assign
        window.location.href = `/${business.domainName}/checkout/edit-guest-profile?${stringify(
          query
        )}`;
      }
    } catch (error) {
      if ((error as lib.SingleResponseProto)?.error?.message) {
        actions.toast.showError((error as lib.SingleResponseProto)?.error?.message ?? '');
      }
      loadCurrentSearch();
    }
  };

  const removeParamsNotRequired = (newQueryParams: SearchQueryStringParams) => {
    //clear out the size parameter for events
    if (newQueryParams?.type === 'EVENT') {
      newQueryParams.size = undefined;
      newQueryParams.time = undefined;
    }
    //case when user switching from event to non-event type.
    //if the current query has end date then clear it out.
    if (newQueryParams?.type !== 'EVENT' && queryStringParams?.type === 'EVENT') {
      newQueryParams.dateEnd = undefined;
    }
  };

  const header = mode?.stateType === 'group' ? mode.businessGroup.name : undefined;

  return {
    selectedBusinessId,
    setSelectedBusinessId,
    isFullMapView,
    openFullMapView,
    closeFullMapView,
    updateSearchUrl,
    navigateToExperience,
    navigateToExperienceMenu,
    getUrlPostfix,
    lockTimeAndSize,
    startOrder,
    mode,
    header,
    analyticsSearchContext: getAnalyticsEventType(mode),
    availability,
    searchQuery,
  };
}
